import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ColumnFiltersState, createColumnHelper } from '@tanstack/react-table';
import { TopUpListItem } from 'models/TopUp';
import { getTopUpsAPI } from 'services/topUpsApi';
import { GlobalSearch } from 'components/GlobalSearch';
import { Filter } from 'components/Filter/Filter';
import { FilterContainer } from 'components/Filter/FilterContainer';
import { DashboardSkeleton } from 'components/Skeleton/DashboardSkeleton';
import { CustomTooltip } from 'components/CustomTooltip';
import { toastWarning } from 'utils/toastMethod';
import CustomTable from 'components/Table/CustomTable';
import PageLayout from 'components/Layout/PageLayout';
import PageLayoutSkeleton from 'components/Layout/PageLayout/skeleton';

type FilterOptions = {
  status: string[];
};

const currencyFormatter = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' });

const formatCurrency = (cents: number) => currencyFormatter.format(cents / 100);

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString('it-IT', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

export default function TopUpsTable() {
  const navigate = useNavigate();
  const [topUps, setTopUps] = useState<TopUpListItem[]>();
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({ status: [] });

  useEffect(() => {
    getTopUpsAPI()
      .then((topUps: TopUpListItem[]) => {
        setTopUps(topUps);
        const temp: FilterOptions = { status: [] };
        topUps.forEach((topUp) => {
          if (!temp.status.includes(topUp.status)) temp.status.push(topUp.status);
        });
        setFilterOptions(temp);
      })
      .catch(() => toastWarning('Qualcosa è andato storto!'));
  }, []);

  const setFilter = (columnId: string, filterValue: string) => {
    setColumnFilters((oldFilters) => {
      const updatedFilters = oldFilters.filter((filter) => filter.id !== columnId);
      if (filterValue) updatedFilters.push({ id: columnId, value: filterValue });
      return updatedFilters;
    });
  };

  const columnHelper = createColumnHelper<TopUpListItem>();
  const columns = [
    columnHelper.accessor('id', {
      header: () => 'ID RICARICA',
      cell: (id) => id.getValue(),
      enableColumnFilter: false,
      enableSorting: false,
    }),
    columnHelper.accessor('amount', {
      header: () => 'IMPORTO',
      cell: (amount) => formatCurrency(amount.getValue() as number),
      enableColumnFilter: false,
      enableSorting: true,
      sortingFn: 'basic',
    }),
    columnHelper.accessor('status', {
      header: () => 'STATO',
      cell: (status) => status.getValue(),
      enableSorting: false,
    }),
    columnHelper.accessor('createdAt', {
      header: () => 'DATA',
      cell: (createdAt) => formatDate(createdAt.getValue() as string),
      enableColumnFilter: false,
      enableSorting: true,
      sortingFn: (rowA, rowB, columnId) => {
        const dateA = new Date(rowA.getValue(columnId) as string);
        const dateB = new Date(rowB.getValue(columnId) as string);
        return dateA.getTime() - dateB.getTime();
      },
    }),
    columnHelper.display({
      header: 'AZIONI',
      cell: ({ row }) => (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <div style={{ cursor: 'pointer' }} onClick={() => navigate(`/topups/${row.original.id}`)}>
            <CustomTooltip id={`TopUpDetailIcon-${row.original.id}`} message="Visualizza dettaglio">
              <span style={{ fontSize: '18px' }}>&#128065;</span>
            </CustomTooltip>
          </div>
        </div>
      ),
    }),
  ];

  return (
    <>
      {topUps ? (
        <PageLayout
          titlePage={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
              <span>Lista ricariche</span>
              <button
                className="btn btn-primary"
                style={{
                  cursor: 'pointer',
                  padding: '10px 16px',
                  borderRadius: '8px',
                  color: '#ffffff',
                  fontSize: '14px',
                  whiteSpace: 'nowrap',
                }}
                onClick={() => navigate('/topups/new')}
              >
                + Nuova ricarica
              </button>
            </div>
          }
        >
          {topUps.length === 0 ? (
            <h4>Non sono presenti ricariche</h4>
          ) : (
            <>
              <GlobalSearch filterValue={globalFilter} onChange={(value) => setGlobalFilter(value)} />
              <FilterContainer>
                <Filter columnId="status" options={filterOptions.status} placeholder="Stato" setFilter={setFilter} />
              </FilterContainer>
              <CustomTable
                data={topUps}
                columns={columns}
                pageStartIndex={0}
                pageMaxSize={10}
                columnsVisibility={{}}
                globalFilter={globalFilter}
                setGlobalFilter={setGlobalFilter}
                columnFilters={columnFilters}
                setColumnFilters={setColumnFilters}
              />
            </>
          )}
        </PageLayout>
      ) : (
        <PageLayoutSkeleton>
          <DashboardSkeleton />
        </PageLayoutSkeleton>
      )}
    </>
  );
}
